import moment from 'moment'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'

import {
	DateRangeCalendar,
	DateRangePicker,
	DateRangePreset
} from '@clickguard/design-system'
import { useStores } from 'hooks'

import { options, ranges } from './constants'
import { GDateRangeProps, Options } from './types'
import { canShiftNext, getPresetInfo, shiftRange } from './utils'

const presets: DateRangePreset[] = options.map((option) => ({
	value: option,
	label: option
}))

export const GDateRange = (props: GDateRangeProps) => {
	const {
		onChange,
		onApply,
		label,
		align = 'left',
		demoMode,
		minDate,
		applyFiltersOnMounting = true,
		applySearchParams = true,
		className
	} = props
	const { userStore, assetStore } = useStores()
	const { user } = userStore
	const [searchParams, setSearchParams] = useSearchParams()

	const startFromUrl = searchParams.get('from')
	const endFromUrl = searchParams.get('to')

	// Minimum selectable date: explicit prop wins, otherwise fall back to the
	// selected website's creation date (production only — demo data predates it),
	// and finally to a permissive floor that preserves the original behavior.
	const assetCreatedAt =
		!assetStore.demoMode && assetStore.asset?.created_at
			? moment(assetStore.asset.created_at).startOf('day').toDate()
			: undefined
	const resolvedMinDate = minDate ?? assetCreatedAt ?? new Date('2000-01-01')
	const clampStart = (d: Date) =>
		moment(d).isBefore(resolvedMinDate) ? new Date(resolvedMinDate) : d

	const initialStart = clampStart(
		startFromUrl
			? moment(Number(startFromUrl)).toDate()
			: moment().subtract(30, 'days').startOf('day').toDate()
	)
	const initialEnd = endFromUrl
		? moment(Number(endFromUrl)).toDate()
		: moment().endOf('day').toDate()

	const [appliedRange, setAppliedRange] = useState({
		startDate: initialStart,
		endDate: initialEnd
	})
	const [pendingRange, setPendingRange] = useState({
		startDate: initialStart,
		endDate: initialEnd,
		key: 'selection'
	})
	const [open, setOpen] = useState(false)
	// Visible calendar month — controlled so preset clicks and reopening the
	// popover navigate the calendar to the pending selection.
	const [month, setMonth] = useState<Date>(initialStart)
	const containerRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<HTMLDivElement>(null)
	const popoverRef = useRef<HTMLDivElement>(null)
	const [popoverPos, setPopoverPos] = useState<{
		top: number
		left: number
	} | null>(null)

	const presetInfo = getPresetInfo(appliedRange.startDate, appliedRange.endDate)

	useEffect(() => {
		if (!applySearchParams) return
		const next = {
			startDate: clampStart(
				startFromUrl
					? moment(Number(startFromUrl)).toDate()
					: moment().subtract(30, 'days').startOf('day').toDate()
			),
			endDate: endFromUrl
				? moment(Number(endFromUrl)).toDate()
				: moment().endOf('day').toDate()
		}
		setAppliedRange(next)
		setPendingRange({ ...next, key: 'selection' })
	}, [startFromUrl, endFromUrl, applySearchParams])

	useEffect(() => {
		if (!applyFiltersOnMounting) return
		onChange?.({ from: appliedRange.startDate, to: appliedRange.endDate })
		onApply?.({ from: appliedRange.startDate, to: appliedRange.endDate })
	}, [])

	useEffect(() => {
		if (!open) return
		const handler = (e: MouseEvent) => {
			const target = e.target as Node
			if (containerRef.current?.contains(target)) return
			if (popoverRef.current?.contains(target)) return
			closePopover()
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [open])

	useLayoutEffect(() => {
		if (!open || !triggerRef.current) return
		const updatePos = () => {
			if (!triggerRef.current) return
			const rect = triggerRef.current.getBoundingClientRect()
			const top = rect.bottom + 8
			const left = align === 'left' ? rect.left : rect.right
			setPopoverPos({ top, left })
		}
		updatePos()
		window.addEventListener('resize', updatePos)
		window.addEventListener('scroll', updatePos, true)
		return () => {
			window.removeEventListener('resize', updatePos)
			window.removeEventListener('scroll', updatePos, true)
		}
	}, [open, align])

	const closePopover = () => {
		setPendingRange({ ...appliedRange, key: 'selection' })
		setOpen(false)
	}

	const persistRange = (next: { startDate: Date; endDate: Date }) => {
		setAppliedRange(next)
		setPendingRange({ ...next, key: 'selection' })
		if (applySearchParams) {
			searchParams.set('from', next.startDate.getTime().toString())
			searchParams.set('to', next.endDate.getTime().toString())
			setSearchParams(searchParams)
		}
		onChange?.({ from: next.startDate, to: next.endDate })
		onApply?.({ from: next.startDate, to: next.endDate })
	}

	// When the website is switched in place, its creation date (and therefore
	// resolvedMinDate) changes — pull any out-of-range applied start forward so
	// the displayed range and downstream data stay within available history.
	useEffect(() => {
		if (moment(appliedRange.startDate).isBefore(resolvedMinDate)) {
			persistRange({
				startDate: new Date(resolvedMinDate),
				endDate: appliedRange.endDate
			})
		}
	}, [resolvedMinDate.getTime()])

	const handlePeriodChange = (option: Options) => {
		if (option === 'Custom') return
		const next = {
			startDate: clampStart(ranges[option].startDate),
			endDate: ranges[option].endDate
		}
		setPendingRange({ ...next, key: 'selection' })
		setMonth(next.startDate)
		onChange?.({ from: next.startDate, to: next.endDate })
	}

	const handleSelectRange = (range?: { from?: Date; to?: Date }) => {
		// Mid-selection react-day-picker reports `to` as undefined — mirror the
		// old single-click behavior by collapsing the range onto the start day.
		if (!range?.from) return
		const next = {
			startDate: range.from,
			endDate: moment(range.to ?? range.from)
				.endOf('day')
				.toDate(),
			key: 'selection'
		}
		setPendingRange(next)
		onChange?.({ from: next.startDate, to: next.endDate })
	}

	const toggleOpen = () => {
		if (!open) setMonth(pendingRange.startDate)
		setOpen(!open)
	}

	const handleApply = () => {
		persistRange({
			startDate: pendingRange.startDate,
			endDate: pendingRange.endDate
		})
	}

	const handleShift = (direction: -1 | 1) => {
		const shifted = shiftRange(
			appliedRange.startDate,
			appliedRange.endDate,
			direction
		)
		if (!shifted) return
		if (direction === -1 && moment(shifted.startDate).isBefore(resolvedMinDate))
			return
		persistRange(shifted)
	}

	const pendingPresetInfo = getPresetInfo(
		pendingRange.startDate,
		pendingRange.endDate
	)
	const prevShift = shiftRange(appliedRange.startDate, appliedRange.endDate, -1)
	const disablePrev =
		!prevShift || moment(prevShift.startDate).isBefore(resolvedMinDate)

	const popoverContent = open && popoverPos && (
		<div
			ref={popoverRef}
			data-cg-portal="daterange"
			className="fixed z-[10000002]"
			style={{
				top: popoverPos.top,
				left: align === 'left' ? popoverPos.left : undefined,
				right:
					align === 'right'
						? Math.max(0, window.innerWidth - popoverPos.left)
						: undefined
			}}
		>
			<DateRangeCalendar
				selected={{ from: pendingRange.startDate, to: pendingRange.endDate }}
				onSelectRange={handleSelectRange}
				month={month}
				onMonthChange={setMonth}
				presets={presets}
				activePreset={pendingPresetInfo.dropdownValue}
				onSelectPreset={(value) => handlePeriodChange(value as Options)}
				timezone={user?.timezone || undefined}
				minDate={resolvedMinDate}
				showActions={!!onApply}
				onApply={() => {
					handleApply()
					setOpen(false)
				}}
				onCancel={closePopover}
				applyDisabled={demoMode}
				applyDisabledReason={
					demoMode
						? 'This action is disabled when demo data is displayed'
						: undefined
				}
			/>
		</div>
	)

	return (
		<div ref={containerRef} className={`flex ${className ?? ''}`}>
			<div>
				{label && (
					<div className="block text-sm font-medium text-t-dark">{label}</div>
				)}
				<div ref={triggerRef} className="flex items-center gap-2">
					<span className="text-base font-medium text-t-coupon whitespace-nowrap">
						{presetInfo.chipLabel}
					</span>
					<DateRangePicker
						startDate={appliedRange.startDate}
						endDate={appliedRange.endDate}
						onTriggerClick={toggleOpen}
						onPrevPeriod={() => handleShift(-1)}
						onNextPeriod={() => handleShift(1)}
						disablePrev={disablePrev}
						disableNext={
							!canShiftNext(appliedRange.startDate, appliedRange.endDate)
						}
					/>
				</div>
			</div>
			<div className="flex-1"></div>
			{popoverContent && createPortal(popoverContent, document.body)}
		</div>
	)
}
